import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { DataAccessPreviewResponse } from '../../types'

interface PreviewResultProps {
    data: DataAccessPreviewResponse
}

const PreviewResult: React.FC<PreviewResultProps> = ({ data }) => {
    const { t } = useTranslation('settings')
    const [empOpen, setEmpOpen] = useState(false)
    const [custOpen, setCustOpen] = useState(false)

    // API may return null for these arrays — normalise so render never crashes
    const employeeSample = data.employee_ids_sample ?? []
    const customerSample = data.customer_ids_sample ?? []
    const rejectColumns = data.reject_columns ?? []

    return (
        <div className="mt-4 space-y-4">
            {/* Tested employee header */}
            <p className="text-sm font-medium text-foreground">
                {t('data-access-preview-as')}{' '}
                <span className="font-semibold">
                    {data.as_employee.name} (#{data.as_employee.id})
                </span>
            </p>

            {/* Count tiles */}
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border bg-muted/40 p-4 text-center">
                    <p className="text-2xl font-bold tabular-nums">
                        {data.employee_ids_count}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('data-access-preview-employee-count')}
                    </p>
                </div>
                <div className="rounded-lg border bg-muted/40 p-4 text-center">
                    <p className="text-2xl font-bold tabular-nums">
                        {data.customer_ids_count}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('data-access-preview-customer-count')}
                    </p>
                </div>
            </div>

            {/* Employee ID sample */}
            {employeeSample.length > 0 && (
                <details
                    open={empOpen}
                    onToggle={(e) =>
                        setEmpOpen((e.target as HTMLDetailsElement).open)
                    }
                    className="border rounded-md"
                >
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium select-none">
                        {t('data-access-preview-employee-sample')}
                    </summary>
                    <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1">
                        {employeeSample.map((id) => (
                            <Badge
                                key={id}
                                variant="outline"
                                className="font-mono text-xs"
                            >
                                #{id}
                            </Badge>
                        ))}
                    </div>
                </details>
            )}

            {/* Customer ID sample */}
            {customerSample.length > 0 && (
                <details
                    open={custOpen}
                    onToggle={(e) =>
                        setCustOpen((e.target as HTMLDetailsElement).open)
                    }
                    className="border rounded-md"
                >
                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium select-none">
                        {t('data-access-preview-customer-sample')}
                    </summary>
                    <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1">
                        {customerSample.map((id) => (
                            <Badge
                                key={id}
                                variant="outline"
                                className="font-mono text-xs"
                            >
                                #{id}
                            </Badge>
                        ))}
                    </div>
                </details>
            )}

            {/* Reject columns */}
            <div>
                <p className="text-xs text-muted-foreground mb-1">
                    {t('data-access-preview-reject-columns')}
                </p>
                {rejectColumns.length === 0 ? (
                    <p className="text-xs italic text-muted-foreground">
                        {t('data-access-preview-no-reject-columns')}
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {rejectColumns.map((col) => (
                            <Badge
                                key={col}
                                variant="secondary"
                                className="font-mono text-xs"
                            >
                                {col}
                            </Badge>
                        ))}
                    </div>
                )}
            </div>

            {/* Helper note */}
            <p className="text-xs text-muted-foreground italic border-t pt-2">
                {t('data-access-preview-helper-note')}
            </p>
        </div>
    )
}

export default PreviewResult
